import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  Input,
  ScrollArea,
  Skeleton,
  Textarea,
} from '@ibsys/kit';
import { TrashIcon } from 'lucide-react';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  BEHAVIOR_FACTOR_TYPES,
  BehaviorFactorFormValues,
  useBehaviorFactor,
  useCreateBehaviorFactor,
  useDeleteBehaviorFactor,
  useUpdateBehaviorFactor,
} from '@/entities/knowledge';
import { hasPredicateIssues, type PredicateValue } from '@/entities/predicate';
import { useHasPermission } from '@/entities/profile';
import { PredicateInput } from '@/features/PredicateInput';
import { useAppNavigate } from '@/shared/lib/ts-router';
import { EditableTitle } from '@/shared/ui';
import { BehaviorFactorSubfactors } from './BehaviorFactorSubfactors';
import { BehaviorFactorParent } from './BehaviorFactorParent';

type BehaviorFactorCardProps = {
  factorId: string;
  onCreateSubfactor: (parentId: number) => void;
  onSelectItem: (id: number) => void;
};

export const BehaviorFactorCard: FC<BehaviorFactorCardProps> = ({ factorId, onCreateSubfactor, onSelectItem }) => {
  const { t } = useTranslation(['common', 'behaviorFactors']);
  const [searchParams] = useSearchParams();
  const navigate = useAppNavigate();
  const allowAdmin = useHasPermission('ADMIN_BEHAVIORAL_FACTORS');

  const isCreate = factorId === 'create';
  const parentIdFromUrl = searchParams.get('parentId');
  const isCreateSubfactor = isCreate && parentIdFromUrl;

  const { data: factor, isLoading } = useBehaviorFactor(isCreate ? undefined : factorId);
  const { mutateAsync: createFactor, isPending: isCreating } = useCreateBehaviorFactor();
  const { mutateAsync: updateFactor, isPending: isUpdating } = useUpdateBehaviorFactor();
  const { mutateAsync: deleteFactor, isPending: isDeleting } = useDeleteBehaviorFactor();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // Введённое условие вместе с проблемами его проверки: в форму уходит только построенное
  // условие, поэтому непригодная запись живёт здесь, а не в значениях формы
  const [predicateValue, setPredicateValue] = useState<PredicateValue | undefined>();
  const hasIssues = hasPredicateIssues(predicateValue);

  const defaultValues = useMemo<BehaviorFactorFormValues>(() => {
    if (factor) {
      return {
        name: factor.name,
        text: factor.text,
        type: factor.type,
        predicate: factor.predicate ? { dsl: factor.predicate.dsl, dslHuman: factor.predicate.dslHuman } : null,
        parentId: factor.parent?.id ?? null,
      };
    }
    return {
      name: '',
      text: '',
      type: isCreateSubfactor ? BEHAVIOR_FACTOR_TYPES.BEHAVIOR_SUBFACTOR : BEHAVIOR_FACTOR_TYPES.BEHAVIOR_FACTOR,
      predicate: null,
      parentId: parentIdFromUrl ? +parentIdFromUrl : null,
    };
  }, [factor, isCreateSubfactor, parentIdFromUrl]);

  const methods = useForm<BehaviorFactorFormValues>({
    defaultValues,
    mode: 'onBlur',
  });

  const { control, handleSubmit, reset, watch, setValue } = methods;

  useEffect(() => {
    reset(defaultValues);
    setPredicateValue(defaultValues.predicate ?? undefined);
  }, [defaultValues, reset]);

  const currentName = watch('name');

  const handleTitleSave = (value: string) => {
    setValue('name', value);
  };

  const handlePredicateChange = (value: PredicateValue) => {
    setPredicateValue(value);
    setValue('predicate', value.dsl ? { dsl: value.dsl, dslHuman: value.dslHuman ?? '' } : null);
  };

  const handleParentChange = (parentId: number | null) => {
    setValue('parentId', parentId);
  };

  const onSubmit = handleSubmit(async (values) => {
    if (hasIssues) {
      return;
    }

    const type =
      isCreateSubfactor || factor?.type === BEHAVIOR_FACTOR_TYPES.BEHAVIOR_SUBFACTOR
        ? BEHAVIOR_FACTOR_TYPES.BEHAVIOR_SUBFACTOR
        : BEHAVIOR_FACTOR_TYPES.BEHAVIOR_FACTOR;

    const payload: BehaviorFactorFormValues = {
      ...values,
      type,
    };

    if (isCreate) {
      const result = await createFactor(payload);
      navigate('/behavior-factors/:id', { id: result.id.toString() });
    } else {
      await updateFactor({ id: factorId, payload });
    }
  });

  const handleDelete = async () => {
    await deleteFactor(factorId);
    setShowDeleteDialog(false);
    navigate('/behavior-factors');
  };

  if (isLoading) {
    return (
      <Card className="w-full h-fit">
        <CardHeader className="border-b">
          <Skeleton className="h-8 w-64" />
        </CardHeader>
        <CardContent className="p-6">
          <Skeleton className="w-full h-40" />
        </CardContent>
      </Card>
    );
  }

  const title = isCreate
    ? isCreateSubfactor
      ? t('behaviorFactors:newSubfactor')
      : t('behaviorFactors:newFactor')
    : currentName;

  const isSubfactor = factor?.type === BEHAVIOR_FACTOR_TYPES.BEHAVIOR_SUBFACTOR || isCreateSubfactor;
  const isSaving = isCreating || isUpdating;
  const canEdit = allowAdmin && !factor?.isDeleted;
  const canDelete = allowAdmin && !isCreate && !factor?.isDeleted;
  const hasSubfactors = factor?.children && factor.children.length > 0;

  return (
    <>
      <Card className="w-full h-full flex flex-col min-h-0">
        <CardHeader className="flex flex-wrap items-center gap-3 border-b">
          {isCreate ? (
            <h2 className="text-lg font-semibold">{title}</h2>
          ) : (
            <EditableTitle
              value={currentName}
              label={t('behaviorFactors:renameFactor')}
              disabled={!canEdit || isSaving}
              onSave={handleTitleSave}
            />
          )}

          <div className="flex gap-4 ml-auto items-center">
            {canDelete && (
              <Button
                size="sm"
                icon={<TrashIcon className="size-4" />}
                variant="outline"
                color="destructive"
                onClick={() => setShowDeleteDialog(true)}
                disabled={isDeleting}
              >
                {t('common:delete')}
              </Button>
            )}
            {canEdit && (
              <Button variant="default" onClick={onSubmit} size="md" isLoading={isSaving} disabled={hasIssues}>
                {t('common:save')}
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-hidden p-0">
          <ScrollArea className="h-full">
            <FormProvider {...methods}>
              <div className="flex flex-col gap-6 p-6">
                {isCreate && (
                  <Input label={t('behaviorFactors:name')} control={control} name="name" required disabled={!canEdit} />
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Textarea
                      label={t('behaviorFactors:text')}
                      control={control}
                      name="text"
                      required
                      disabled={!canEdit}
                      className="min-h-32"
                      placeholder={t('behaviorFactors:textPlaceholder')}
                    />
                  </div>

                  <div>
                    <PredicateInput
                      value={predicateValue}
                      onChange={handlePredicateChange}
                      disabled={!canEdit}
                      classNames={{ input: 'min-h-32' }}
                    />
                  </div>
                </div>

                {/* Relations section */}
                {isSubfactor && (
                  <BehaviorFactorParent
                    parentId={watch('parentId')}
                    parentName={factor?.parent?.name}
                    onParentChange={handleParentChange}
                    onSelectParent={onSelectItem}
                    disabled={!canEdit}
                  />
                )}

                {!isSubfactor && !isCreate && (
                  <BehaviorFactorSubfactors
                    subfactors={factor?.children ?? []}
                    onAddSubfactor={() => onCreateSubfactor(+factorId)}
                    onSelectSubfactor={onSelectItem}
                    disabled={!canEdit}
                  />
                )}
              </div>
            </FormProvider>
          </ScrollArea>
        </CardContent>
      </Card>

      <DialogRoot open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('behaviorFactors:deleteTitle')}</DialogTitle>
            <DialogDescription>
              {hasSubfactors ? (
                t('behaviorFactors:deleteHasSubfactors')
              ) : (
                <Trans
                  i18nKey="behaviorFactors:deleteConfirm"
                  values={{ name: factor?.name }}
                  components={{ strong: <strong /> }}
                />
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              {t('common:cancel')}
            </Button>
            <Button variant="outline" color="destructive" onClick={handleDelete} disabled={isDeleting || hasSubfactors}>
              {t('common:delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </>
  );
};
